/**
 * InputTypes API functions
 */

import { apiClient, buildQueryString } from "@/shared/lib/api/client";
import type {
  InputType,
  InputTypeListItem,
  InputTypeListResult,
  InputTypeCreateInput,
  InputTypeUpdateInput,
  SuggestInputTypeRequest,
  SuggestInputTypeResponse,
  RefineInputTypeRequest,
  RefineInputTypeResponse,
  ConfirmInputTypeRequest,
} from "../types";

const BASE_PATH = "/api/input-types";

// ===================
// CRUD Operations
// ===================

export async function getInputTypes(params?: {
  skip?: number;
  limit?: number;
}): Promise<InputTypeListResult> {
  const query = buildQueryString({
    skip: params?.skip ?? 0,
    limit: params?.limit ?? 50,
  });
  return apiClient<InputTypeListResult>(`${BASE_PATH}${query}`);
}

export async function getInputType(id: string): Promise<InputType> {
  return apiClient<InputType>(`${BASE_PATH}/${id}`);
}

export async function createInputType(data: InputTypeCreateInput): Promise<InputType> {
  return apiClient<InputType>(BASE_PATH, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateInputType(
  id: string,
  data: InputTypeUpdateInput
): Promise<InputType> {
  return apiClient<InputType>(`${BASE_PATH}/${id}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteInputType(id: string): Promise<void> {
  return apiClient<void>(`${BASE_PATH}/${id}`, {
    method: "DELETE",
  });
}

// ===================
// Search
// ===================

export async function searchInputTypes(
  query: string,
  limit = 10
): Promise<{ items: InputTypeListItem[] }> {
  const params = buildQueryString({ q: query, limit });
  return apiClient<{ items: InputTypeListItem[] }>(`${BASE_PATH}/search${params}`);
}

export async function findSimilarInputTypes(
  text: string,
  limit = 5
): Promise<{ query: string; items: Array<InputTypeListItem & { score: number }> }> {
  const params = buildQueryString({ text, limit });
  return apiClient(`${BASE_PATH}/similar${params}`);
}

export interface ReverseSearchResult {
  input_type_id: string;
  input_type_title: string;
  field_name: string;
  match_count?: number;
  value?: number;
  unit?: string;
  similarity?: number;
}

export interface ReverseSearchResponse {
  query_type: "numeric" | "semantic";
  query_value?: number;
  query_text?: string;
  tolerance?: number;
  items: ReverseSearchResult[];
}

export async function reverseSearchInputTypes(params: {
  value?: number;
  text?: string;
  tolerance?: number;
  limit?: number;
}): Promise<ReverseSearchResponse> {
  const query = buildQueryString({
    value: params.value,
    text: params.text,
    tolerance: params.tolerance ?? 0,
    limit: params.limit ?? 10,
  });
  return apiClient<ReverseSearchResponse>(`${BASE_PATH}/reverse-search${query}`);
}

// ===================
// Suggestion Flow
// ===================

export async function suggestInputType(
  data: SuggestInputTypeRequest
): Promise<SuggestInputTypeResponse> {
  return apiClient<SuggestInputTypeResponse>(`${BASE_PATH}/suggest`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function refineInputTypeSuggestion(
  data: RefineInputTypeRequest
): Promise<RefineInputTypeResponse> {
  return apiClient<RefineInputTypeResponse>(`${BASE_PATH}/refine`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function confirmInputType(
  data: ConfirmInputTypeRequest
): Promise<InputType> {
  return apiClient<InputType>(`${BASE_PATH}/confirm`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ===================
// Related Data
// ===================

export async function getInputTypeEntries(
  inputTypeId: string,
  params?: { skip?: number; limit?: number; includeSubtypes?: boolean }
): Promise<{
  input_type_id: string;
  input_type_title: string;
  items: Array<{
    id: string;
    full_description: string;
    start_time: string | null;
    end_time: string | null;
    duration_minutes: number | null;
    is_running: boolean;
    recorded_at: string;
  }>;
  skip: number;
  limit: number;
  include_subtypes?: boolean;
  has_more?: boolean;
  total?: number;
}> {
  const query = buildQueryString({
    skip: params?.skip ?? 0,
    limit: params?.limit ?? 50,
    ...(params?.includeSubtypes ? { include_subtypes: true } : {}),
  });
  return apiClient(`${BASE_PATH}/${inputTypeId}/entries${query}`);
}

export async function getInputTypeHierarchy(inputTypeId: string): Promise<{
  id: string;
  title: string;
  parent: InputTypeListItem | null;
  children: InputTypeListItem[];
}> {
  // Backend returns different field names, so we need to map them
  const response = await apiClient<{
    id: string;
    title: string;
    parent_type_id?: string;
    parent_type_title?: string;
    child_types?: InputTypeListItem[];
  }>(`${BASE_PATH}/${inputTypeId}/hierarchy`);
  
  // Get parent details if parent exists
  let parent: InputTypeListItem | null = null;
  if (response.parent_type_id) {
    // Fetch full parent details
    try {
      const parentData = await apiClient<InputTypeListItem>(`${BASE_PATH}/${response.parent_type_id}`);
      parent = parentData;
    } catch {
      // If parent fetch fails, create minimal object
      parent = {
        id: response.parent_type_id,
        title: response.parent_type_title || "Unknown",
        slug: "",
        description: null,
        allows_concurrent: false,
        requires_duration: false,
        ui_mode: "schema_driven",
        custom_view_path: null,
        icon: null,
        color: null,
        generated_icon_url: null,
        icon_generation_status: null,
        entry_count: 0,
        last_entry_at: null,
      };
    }
  }
  
  return {
    id: response.id,
    title: response.title,
    parent,
    children: response.child_types || [],
  };
}

// ===================
// Subtype Creation
// ===================

export interface CreateSubtypeRequest {
  parent_type_id: string;
  title: string;
  short_title?: string;
  description?: string;
  prefilled_values?: Record<string, unknown>;
  data_schema?: Record<string, unknown>;
}

export async function createSubtype(data: CreateSubtypeRequest): Promise<{
  success: boolean;
  subtype: InputType;
}> {
  return apiClient(`/api/schema-evolution/create-subtype`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ===================
// Duplicate Check
// ===================

export interface InputTypeDuplicateCheckResult {
  duplicates: Array<{
    id: string;
    title: string;
    description: string | null;
    similarity: number;
  }>;
  has_duplicates: boolean;
  threshold: number;
  error?: string;
}

// Icon backfill + regenerate moved to /api/admin/input-types/* in
// 2026-06-23. See clients/web/src/features/admin/api/index.ts
// (getAdminIconStatus / adminBackfillIcons / adminRegenerateFailedIcons /
// adminRegenerateIcon).

export async function checkInputTypeDuplicates(params: {
  title: string;
  description?: string;
  threshold?: number;
}): Promise<InputTypeDuplicateCheckResult> {
  const query = buildQueryString({
    title: params.title,
    description: params.description,
    threshold: params.threshold ?? 0.85,
  });
  return apiClient<InputTypeDuplicateCheckResult>(`${BASE_PATH}/check-duplicates${query}`, {
    method: "POST",
  });
}
